/**
 * Destination guide — generates culturally rich, educational content about a
 * tour's destination using Lovable AI. The result is cached server-side per
 * (title + location + lang) so repeated visits are cheap.
 *
 * Output shape is intentionally compact so the page can render it as a
 * "mini-guide" with sections + curiosity cards ("Sabías que...").
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

export type GuideSectionKey =
  | "history"
  | "culture"
  | "traditions"
  | "fauna_flora"
  | "gastronomy"
  | "legends"
  | "customs"
  | "geography";

export interface GuideSection {
  key: GuideSectionKey;
  title: string;
  body: string;
  /** Lugar del itinerario al que pertenece este módulo (guías por tour). */
  place?: string;
}

export interface DestinationGuide {
  intro: string;
  sections: GuideSection[];
  didYouKnow: string[];
  /** Detected destination name (e.g. "Cozumel"). Useful for the assistant. */
  destination: string;
}

interface CacheEntry {
  value: DestinationGuide;
  expires: number;
}
const cache = new Map<string, CacheEntry>();
const TTL_MS = 24 * 60 * 60 * 1000; // 24h (memoria del isolate)
const DB_TTL_DAYS = 90; // caché persistente: el mismo destino no se paga dos veces

async function readDbCache(cacheKey: string): Promise<DestinationGuide | null> {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("ai_content_cache")
      .select("payload, expires_at")
      .eq("cache_key", cacheKey)
      .maybeSingle();
    if (!data?.payload) return null;
    if (data.expires_at && new Date(data.expires_at as string).getTime() < Date.now()) return null;
    return data.payload as unknown as DestinationGuide;
  } catch {
    return null;
  }
}

async function writeDbCache(cacheKey: string, lang: string, guide: DestinationGuide): Promise<void> {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.from("ai_content_cache").upsert(
      {
        cache_key: cacheKey,
        kind: "destination_guide",
        lang,
        payload: guide as unknown as never,
        expires_at: new Date(Date.now() + DB_TTL_DAYS * 24 * 3600_000).toISOString(),
      },
      { onConflict: "cache_key" },
    );
  } catch {
    /* el caché es best-effort */
  }
}

const ALLOWED_KEYS: GuideSectionKey[] = [
  "history",
  "culture",
  "traditions",
  "fauna_flora",
  "gastronomy",
  "legends",
  "customs",
  "geography",
];

function fallback(title: string, location: string | null, lang: string = "es"): DestinationGuide {
  const dest = location || title;
  return {
    destination: dest,
    intro: lang === "fr"
      ? `${dest} est l'une des destinations les plus fascinantes du Mexique. Chaque recoin recèle une histoire, une culture et une nature qui ne demandent qu'à être découvertes.`
      : `${dest} es uno de los destinos más fascinantes de México. Cada rincón guarda historia, cultura y naturaleza esperando a ser descubiertos.`,
    sections: [],
    didYouKnow: [],
  };
}

const LANG_INSTRUCTION: Record<string, string> = {
  en: "Respond in English.",
  fr: "Réponds en français.",
  pt: "Responda em português.",
  it: "Rispondi in italiano.",
  de: "Antworte auf Deutsch.",
  es: "Responde en español neutro de México.",
};

/** Guía de UN lugar concreto, reutilizable entre tours que lo visitan. */
async function getPlaceGuide(
  place: string,
  context: string,
  lang: string,
  generate: boolean,
): Promise<DestinationGuide | null> {
  const cacheKey = `place:v1::${lang}::${place.toLowerCase()}`;
  const hit = cache.get(cacheKey);
  if (hit && hit.expires > Date.now()) return hit.value;
  const stored = await readDbCache(cacheKey);
  if (stored) {
    cache.set(cacheKey, { value: stored, expires: Date.now() + TTL_MS });
    return stored;
  }
  const apiKey = process.env.LOVABLE_API_KEY;
  if (!apiKey || !generate) return null;

  const system = `Eres un guía cultural experto de México. Escribe SOLO sobre el lugar indicado: "${place}". No menciones ni describas otros destinos (por ejemplo Chichén Itzá, Tulum, Bacalar o Cancún) salvo que sean parte del propio lugar. ${LANG_INSTRUCTION[lang] ?? LANG_INSTRUCTION.es} Nada de hoteles, restaurantes, tours ni promoción. Si no tienes certeza sobre un dato, omítelo. Devuelve SIEMPRE JSON válido.`;
  const user = `Lugar: "${place}"
Contexto del lugar (región): ${context || "México"}

Devuelve un JSON con EXACTAMENTE esta forma:
{
  "intro": "1-2 frases sobre ${place}",
  "sections": [{ "key": "history" | "culture" | "traditions" | "fauna_flora" | "gastronomy" | "legends" | "customs" | "geography", "title": "Título breve", "body": "2-3 frases con datos concretos de ${place}" }],
  "didYouKnow": ["Dato curioso verificable de ${place}"]
}
Reglas:
- Entre 2 y 3 "sections", SOLO las categorías que realmente apliquen a ${place} (no rellenes categorías).
- Entre 1 y 2 "didYouKnow", máx ~140 caracteres.
- Habla del lugar, no del tour.`;
  try {
    const resp = await fetch("https://ai.gateway.lovable.dev/v1/responses", {
      method: "POST",
      headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "openai/gpt-6-astra",
        instructions: system,
        input: user,
        text: { format: { type: "json_object" } },
      }),
    });
    if (!resp.ok) {
      console.warn("[place-guide] gateway", resp.status, await resp.text().catch(() => ""));
      return null;
    }
    const json: any = await resp.json();
    const text: string =
      json?.output_text ??
      (json?.output ?? [])
        .flatMap((o: any) => o?.content ?? [])
        .map((c: any) => c?.text ?? "")
        .join("");
    const parsed: any = JSON.parse(text || "null");
    if (!parsed) return null;
    const guide: DestinationGuide = {
      destination: place,
      intro: typeof parsed.intro === "string" ? parsed.intro.slice(0, 400) : "",
      sections: (Array.isArray(parsed.sections) ? parsed.sections : [])
        .filter((s: any) => s && typeof s.title === "string" && typeof s.body === "string" && ALLOWED_KEYS.includes(s.key))
        .slice(0, 3)
        .map((s: any) => ({ key: s.key, title: String(s.title).slice(0, 120), body: String(s.body).slice(0, 700), place })),
      didYouKnow: (Array.isArray(parsed.didYouKnow) ? parsed.didYouKnow : [])
        .filter((d: any) => typeof d === "string" && d.trim())
        .slice(0, 2)
        .map((d: string) => d.slice(0, 220)),
    };
    cache.set(cacheKey, { value: guide, expires: Date.now() + TTL_MS });
    await writeDbCache(cacheKey, lang, guide);
    return guide;
  } catch {
    return null;
  }
}

export const getDestinationGuide = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      title: z.string().min(1).max(300),
      location: z.string().max(200).nullable().optional(),
      summary: z.string().max(2000).nullable().optional(),
      lang: z.enum(["es", "en", "fr", "pt", "it", "de"]).optional(),
      /**
       * Lugares reales del itinerario del tour (ver tour-places.ts). Si se
       * envía, la guía se construye por lugar y nunca se cae a la guía
       * genérica del título/ciudad de salida. Lista vacía = sin guía.
       */
      places: z.array(z.string().trim().min(1).max(80)).max(8).optional(),
      /**
       * COSTE IA: solo generamos contenido nuevo cuando el viajero lo pide
       * explícitamente (abrir la mini-guía, pulsar play…). Sin este flag
       * devolvemos lo que ya esté en caché, y si no hay nada, el texto base.
       */
      generate: z.boolean().optional(),
    }),
  )
  .handler(async ({ data }): Promise<{ guide: DestinationGuide; generated: boolean }> => {
    const lang = data.lang ?? "es";
    const location = data.location ?? null;

    if (data.places) {
      const region = (location ?? "").split(",").slice(1).join(",").trim();
      const parts = await Promise.all(
        data.places.map((p) => getPlaceGuide(p, region, lang, data.generate === true)),
      );
      const ok = parts.filter((g): g is DestinationGuide => !!g);
      return {
        guide: {
          destination: data.places.join(" · "),
          intro: ok.map((g) => g.intro).filter(Boolean).join(" "),
          sections: ok.flatMap((g) => g.sections.map((s) => ({ ...s, place: g.destination }))),
          didYouKnow: ok.flatMap((g) => g.didYouKnow),
        },
        generated: ok.length > 0,
      };
    }

    const cacheKey = `${lang}::${(location ?? "").toLowerCase()}::${data.title.toLowerCase()}`;
    const hit = cache.get(cacheKey);
    if (hit && hit.expires > Date.now()) return { guide: hit.value, generated: false };


    const stored = await readDbCache(cacheKey);
    if (stored) {
      cache.set(cacheKey, { value: stored, expires: Date.now() + TTL_MS });
      return { guide: stored, generated: false };
    }

    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey || data.generate !== true) {
      return { guide: fallback(data.title, location, lang), generated: false };
    }

    const langInstruction =
      lang === "en"
        ? "Respond in English."
        : lang === "fr"
          ? "Réponds en français."
          : lang === "pt"
            ? "Responda em português."
            : lang === "it"
              ? "Rispondi in italiano."
              : "Responde en español neutro de México.";

    const system = `Eres un guía cultural experto de México (historiador, biólogo y narrador). Tu misión es transformar la página de un tour en una mini-guía educativa del destino: historia profunda, cultura viva, tradiciones, fauna/flora, gastronomía típica, leyendas locales, costumbres, datos geográficos y curiosidades verificables. ${langInstruction} NUNCA recomiendes restaurantes específicos, hoteles, otros tours, ni hagas promoción comercial. Sé concreto, evocador y educativo. Si no tienes certeza sobre un dato, omítelo. Devuelve SIEMPRE JSON válido siguiendo el esquema solicitado.`;

    const user = `Tour: "${data.title}"
Ubicación: ${location ?? "México"}
${data.summary ? `Resumen: ${data.summary}` : ""}

Identifica el destino principal (ciudad, isla, sitio arqueológico, ecosistema) al que se refiere este tour y genera una mini-guía cultural del destino.

Devuelve un JSON con EXACTAMENTE esta forma:
{
  "destination": "Nombre del destino principal (ej. 'Cozumel', 'Tulum', 'Holbox')",
  "intro": "Un párrafo evocador de 2-3 frases que invite a conocer el destino. Sin clichés turísticos.",
  "sections": [
    {
      "key": "history" | "culture" | "traditions" | "fauna_flora" | "gastronomy" | "legends" | "customs" | "geography",
      "title": "Título breve y atractivo",
      "body": "2-4 frases con datos concretos, fechas, nombres, especies, platillos típicos, etc. Sin recomendaciones comerciales."
    }
  ],
  "didYouKnow": [
    "Dato curioso 1 (corto, sorprendente, verificable)",
    "Dato curioso 2",
    "..."
  ]
}

Reglas:
- Incluye entre 5 y 7 "sections", priorizando las que mejor describan ESTE destino (no inventes categorías irrelevantes).
- Incluye entre 5 y 7 "didYouKnow", cortos (máx ~140 caracteres cada uno).
- Nada de listas de hoteles, restaurantes, tours, agencias ni precios.
- Habla del lugar, no del tour.`;

    try {
      const resp = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${apiKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: "google/gemini-2.5-flash",
          messages: [
            { role: "system", content: system },
            { role: "user", content: user },
          ],
          response_format: { type: "json_object" },
          temperature: 0.7,
        }),
      });

      if (!resp.ok) {
        console.warn("[destination-guide] gateway", resp.status, await resp.text().catch(() => ""));
        return { guide: fallback(data.title, location, lang), generated: false };
      }

      const json: any = await resp.json();
      const content = json?.choices?.[0]?.message?.content;
      if (typeof content !== "string") return { guide: fallback(data.title, location, lang), generated: false };

      let parsed: any;
      try {
        parsed = JSON.parse(content);
      } catch {
        return { guide: fallback(data.title, location, lang), generated: false };
      }

      const sections: GuideSection[] = Array.isArray(parsed?.sections)
        ? parsed.sections
            .filter(
              (s: any) =>
                s &&
                typeof s.title === "string" &&
                typeof s.body === "string" &&
                ALLOWED_KEYS.includes(s.key),
            )
            .slice(0, 8)
            .map((s: any) => ({
              key: s.key as GuideSectionKey,
              title: String(s.title).slice(0, 120),
              body: String(s.body).slice(0, 800),
            }))
        : [];

      const didYouKnow: string[] = Array.isArray(parsed?.didYouKnow)
        ? parsed.didYouKnow
            .filter((d: any) => typeof d === "string" && d.trim())
            .slice(0, 8)
            .map((d: string) => d.slice(0, 220))
        : [];

      const guide: DestinationGuide = {
        destination:
          typeof parsed?.destination === "string" && parsed.destination.trim()
            ? parsed.destination.trim().slice(0, 80)
            : location ?? data.title,
        intro:
          typeof parsed?.intro === "string" && parsed.intro.trim()
            ? parsed.intro.slice(0, 500)
            : fallback(data.title, location, lang).intro,
        sections,
        didYouKnow,
      };

      cache.set(cacheKey, { value: guide, expires: Date.now() + TTL_MS });
      await writeDbCache(cacheKey, lang, guide);
      return { guide, generated: true };
    } catch (e) {
      console.warn("[destination-guide] error", e);
      return { guide: fallback(data.title, location, lang), generated: false };
    }
  });
